import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Loader2,
  ScanLine,
  AlertTriangle,
  CloudOff,
  RefreshCw,
  CheckCircle2,
  Check,
} from 'lucide-react';
import DatePicker from '../DatePicker';
import PhotoUploader from '../PhotoUploader';
import TicketSelect from './TicketSelect';
import { api, getApiErrorMessage } from '../../api/client';
import { unwrapArray } from '../../lib/api-envelope';
import { useSyncStore } from '../../lib/offline/sync-store';
import { jobLabelOr } from '../../lib/job-label';
import { parseMoney } from '../../lib/display-labels';
import { LOAD_FAILURE_QUERY_OPTIONS, queryLoadFailure } from '../../lib/load-failure';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { formatInstantDay } from '../../lib/calendar-date';
import { salesActivityKindLabel } from '../../lib/sales-activity';
import type { SalesActivity } from '../../types/sales-activity';
import {
  EXPENSE_CATEGORIES,
  EXPENSE_CATEGORY_LABELS,
  OCR_MESSAGES,
  classifyOcrError,
  classifyOcrResponse,
  type ExpenseCategory,
  type ExpenseKind,
  EXPENSE_KIND_LABELS,
  PAID_BY_VALUES,
  PAID_BY_LABELS,
  PAID_BY_CONSEQUENCE,
  type ExpenseCreateBody,
  type OcrOutcome,
  type OcrPrefill,
  type PaidBy,
} from '../../lib/expenses';

interface ReceiptPhoto {
  key: string;
  url: string;
  label?: string;
}

type ScanState =
  | { kind: 'idle' }
  | { kind: 'linking' }
  | { kind: 'running' }
  | { kind: 'unlinked'; message: string }
  | OcrOutcome;

const inputClass =
  'w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white';

/**
 * Photograph a receipt, let the scan prefill, confirm, file. The body of the
 * capture screen, shared by the field tree (`/field/expenses/new`) and the
 * office tree (`/expenses/new`) since FE-1 — the screens own only their chrome
 * and where a filed receipt sends you (`onFiled`).
 *
 * ── WHAT WAS THIS FOR? (office only, API-1 / 0501) ───────────────────────────
 * `mode: 'office'` adds "On a job" | "General / sales". General is a receipt
 * with NO job — a sales lunch — and the API refuses a job_id or ticket_id on
 * one, so General hides both pickers and offers an optional CUSTOMER instead.
 * The body then carries `expense_kind: 'general'` and no `job_id` key at all
 * (never `''`: the API reads any job_id on a general expense as a 400).
 * The photo has no job to be keyed to, so a general receipt is presigned
 * against the FILER'S OWN user id — the second claim API-1 added to
 * `expense_receipt`. Switching kind discards a photo already taken, because it
 * was filed against the other entity and the scan looks it up by that entity.
 *
 * FIELD HANDS NEVER SEE GENERAL (D2, decided 2026-10-07): field receipts stay
 * 100% job-tied, so `mode: 'field'` renders exactly what it rendered before.
 * The office picks from the company's jobs (`GET /jobs`); the field picks from
 * the crew's own (`/employee/my-jobs`) and prefills from the clock.
 *
 *
 * ONE control does the camera: PhotoUploader. It is the only component in the
 * repo that gets camera, presign, `res.ok` on the PUT, and the offline queue
 * right, and the two screens that rolled their own (AssetExpenses, Fleet
 * Expenses) ended up with a mislabelled file picker and buttons with no
 * handler. The maintenance dialog got the camera right and then split it: an
 * OCR input whose photo was thrown away, and an uploader whose photo was never
 * scanned. Here the scan runs on the STORED photo — the row PhotoUploader
 * just confirmed — so the bytes the office sees are the bytes the scan read.
 *
 * The photo is keyed `expense_receipt` + the JOB id. Uploads need a real
 * entity id before the uploader is interactive and the expense does not exist
 * yet, so the API's contract is: presign under the job, then POST /expenses
 * re-points the row to the new expense. That is why the uploader waits for a
 * job to be chosen — there is nothing to key the photo to until then.
 *
 * WHO PAID is asked FIRST, above the camera. A hand knows the answer before
 * they take the photo — it is the one fact about a receipt they cannot get
 * wrong — and everything downstream branches on it: a company-card receipt is
 * filed for cost and never reaches payroll. Personal is the default because it
 * is the ordinary case and it is the column default, so a hand who does not
 * think about the control files exactly what they would have filed before it
 * existed. What each choice MEANS is printed under it, because the difference
 * is money and the words "personal" and "company" alone do not say that.
 *
 * Manual entry is never gated behind the scan. Every scan outcome leaves the
 * form editable; the scan only ever fills fields that are still empty.
 *
 * The TICKET is optional and belongs to the job. It is offered once a job is
 * chosen, from that job's own tickets, and cleared whenever the job moves —
 * the API refuses a mismatched pair with a 400 naming both jobs, and a picker
 * that can produce that 400 is a picker offering an error. A job with no
 * ticket yet is the ordinary case, not a blocked one: it says so and the
 * receipt files without one.
 */
interface ExpenseCaptureFormProps {
  mode: 'field' | 'office';
  /** Called after a successful file. The screen invalidates and navigates. */
  onFiled: () => void;
  className?: string;
  /**
   * The filer's own user id — what a GENERAL receipt's photo is presigned
   * against. Passed in rather than read from useAuth so the field tree (which
   * never files General) renders without an auth provider in its tests.
   */
  currentUserId?: string | null;
  /**
   * Offer the optional Meeting picker on a general receipt (CRM-FE-2). Only a
   * CRM role may read /sales-activities, so the screen decides; the field tree
   * never passes it.
   */
  meetingPicker?: boolean;
}

export default function ExpenseCaptureForm({ mode, onFiled, className, currentUserId, meetingPicker = false }: ExpenseCaptureFormProps) {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const isOnline = useSyncStore((s) => s.isOnline);
  const isOffice = mode === 'office';
  const timeZone = useTenantTimeZone();
  // 'job' is the only kind the field tree can produce (D2). An office link may
  // open General with the customer and meeting already chosen — "Add a receipt
  // for this meeting" (CRM-FE-2) passes ?kind=general&customer=&meeting=.
  const [kind, setKind] = useState<ExpenseKind>(
    isOffice && searchParams.get('kind') === 'general' ? 'general' : 'job',
  );
  const isGeneral = isOffice && kind === 'general';
  const [customerId, setCustomerId] = useState<string>(isGeneral ? searchParams.get('customer') ?? '' : '');
  const [meetingId, setMeetingId] = useState<string>(
    isGeneral && meetingPicker ? searchParams.get('meeting') ?? '' : '',
  );
  // The chosen meeting's customer, held beside it: the meeting list refetches
  // whenever the customer moves, so it cannot be the thing asked.
  const [meetingCustomerId, setMeetingCustomerId] = useState<string>(meetingId ? customerId : '');

  const [photos, setPhotos] = useState<ReceiptPhoto[]>([]);
  const [receiptUploadId, setReceiptUploadId] = useState<string | null>(null);
  const [scannedKey, setScannedKey] = useState<string | null>(null);
  const [scan, setScan] = useState<ScanState>({ kind: 'idle' });

  const [jobId, setJobId] = useState<string>(searchParams.get('job') ?? '');
  const [ticketId, setTicketId] = useState<string | null>(null);
  // 'personal' is the ordinary case and the API's column default; a hand who
  // never touches this control files what they would have filed before it.
  const [paidBy, setPaidBy] = useState<PaidBy>('personal');
  const [vendor, setVendor] = useState('');
  const [date, setDate] = useState('');
  const [amount, setAmount] = useState('');
  const [gst, setGst] = useState('');
  const [category, setCategory] = useState<ExpenseCategory | ''>('');
  const [description, setDescription] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  /**
   * The ticket belongs to the job. Moving the job and keeping the ticket is
   * the pair the API refuses with a 400 naming both jobs, so the choice is
   * cleared here rather than carried into a request that cannot succeed.
   * Every path that sets the job goes through this — the picker and the
   * clocked-in fallback alike. Declared above the effect that depends on it:
   * a dep array is read during render, so a `useCallback` below it is a TDZ
   * throw, not a late binding.
   */
  const selectJob = useCallback((next: string) => {
    setJobId(next);
    setTicketId(null);
  }, []);

  // The job is in context wherever a hand would photograph a receipt: the job
  // screen passes ?job=, and a clocked-in hand is on the timesheet's job. Only
  // when neither says is the picker the way in.
  const { data: jobs = [] } = useQuery({
    // The crew's own jobs in the field; the company's in the office, where a
    // filer is usually on no crew at all (TNT's salesperson has 0 assignments).
    queryKey: isOffice ? ['office-expense-jobs'] : ['field-expense-jobs'],
    queryFn: () =>
      (isOffice ? api.jobs.list() : api.employee.myJobs()).then((r) => unwrapArray<any>(r.data)),
  });
  const { data: timesheetStatus } = useQuery({
    queryKey: ['timesheet-status'],
    queryFn: () => api.timesheets.status().then((r) => r.data?.data ?? r.data),
    staleTime: 10000,
    // The clocked-in prefill is a field idea. An office filer is not on a shift.
    enabled: !isOffice,
  });
  // Optional on a general receipt. Loaded only when it can be shown; a load
  // that failed is said so, never rendered as "no customers" (lib/load-failure).
  const customersQuery = useQuery({
    queryKey: ['expense-capture-customers'],
    queryFn: () => api.customers.list().then((r) => unwrapArray<any>(r.data)),
    enabled: isGeneral,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const customers = customersQuery.data ?? [];
  const customersFailure = queryLoadFailure(customersQuery);

  /**
   * The meeting this receipt was for — optional. With a customer chosen, that
   * customer's confirmed meetings; without one, the filer's own (mine=1), and
   * picking a meeting fills its customer. Newest first is the API's order.
   * The API refuses a meeting of a different customer, so moving the customer
   * clears a meeting that no longer matches.
   */
  const meetingsQuery = useQuery({
    queryKey: ['expense-capture-meetings', customerId || 'mine'],
    queryFn: () =>
      api.salesActivities
        .list(customerId ? { customer_id: customerId } : { mine: 1 })
        .then((r) => unwrapArray<SalesActivity>(r.data)),
    enabled: isGeneral && meetingPicker,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const meetings = meetingsQuery.data ?? [];
  const meetingsFailure = queryLoadFailure(meetingsQuery);
  const selectCustomer = (next: string) => {
    setCustomerId(next);
    if (meetingId && meetingCustomerId !== next) {
      setMeetingId('');
      setMeetingCustomerId('');
    }
  };
  const selectMeeting = (next: string) => {
    setMeetingId(next);
    const m = meetings.find((x) => x.id === next);
    setMeetingCustomerId(m?.customerId ?? '');
    if (m) setCustomerId(m.customerId);
  };
  useEffect(() => {
    if (isOffice) return;
    if (jobId) return;
    const activeJobId = timesheetStatus?.timesheet?.job_id ?? timesheetStatus?.timesheet?.jobId;
    if (activeJobId) selectJob(String(activeJobId));
  }, [isOffice, jobId, timesheetStatus, selectJob]);

  const jobFromParam = searchParams.get('job');
  const selectedJob = useMemo(() => jobs.find((j: any) => j.id === jobId) ?? null, [jobs, jobId]);

  const photo = photos[0] ?? null;
  const photoPending = !!photo && photo.key.startsWith('pending-');
  /**
   * What the receipt photo is filed against: the job, or — for a general
   * receipt, which has none — the filer themself. The API's expense_receipt
   * claim accepts both (0501), and POST /expenses re-points the row either way.
   */
  const receiptEntityId: string = isGeneral ? (currentUserId ?? '') : jobId;

  const applyPrefill = useCallback((p: OcrPrefill) => {
    setVendor((v) => v || (p.vendor ?? ''));
    setDate((d) => d || (p.date ?? ''));
    setAmount((a) => a || (p.amount !== null ? String(p.amount) : ''));
    setGst((g) => g || (p.gst !== null ? String(p.gst) : ''));
    setCategory((c) => c || (p.category ?? ''));
  }, []);

  /** Find the uploads row for the confirmed photo, then scan it. */
  const linkAndScan = useCallback(
    async (key: string) => {
      setScan({ kind: 'linking' });
      let uploadId: string | null = null;
      try {
        // Every receipt any hand has presigned under this job comes back;
        // the one that is ours is the one whose object key we just wrote.
        const res = await api.uploads.listForEntity('expense_receipt', receiptEntityId);
        const rows = unwrapArray<any>(res.data);
        const row = rows.find((u) => (u?.s3Key ?? u?.s3_key ?? u?.key) === key);
        uploadId = row?.id ?? null;
      } catch (err) {
        setScan({ kind: 'unlinked', message: getApiErrorMessage(err, 'The photo uploaded but could not be linked. Retry.') });
        return;
      }
      if (!uploadId) {
        setScan({ kind: 'unlinked', message: 'The photo uploaded but could not be linked. Retry.' });
        return;
      }
      setReceiptUploadId(uploadId);

      setScan({ kind: 'running' });
      try {
        const res = await api.expenses.ocr({ receipt_upload_id: uploadId });
        const outcome = classifyOcrResponse(res.data);
        if (outcome.kind === 'ok') applyPrefill(outcome.prefill);
        setScan(outcome);
      } catch (err) {
        setScan(classifyOcrError(err));
      }
    },
    [receiptEntityId, applyPrefill],
  );

  // A freshly confirmed photo (not a queued one) gets scanned exactly once.
  useEffect(() => {
    if (!photo || photoPending || photo.key === scannedKey) return;
    setScannedKey(photo.key);
    linkAndScan(photo.key);
  }, [photo, photoPending, scannedKey, linkAndScan]);

  const retake = () => {
    // The abandoned upload stays under the job, unattached. The API refuses a
    // receipt only when it is attached to ANOTHER expense, so it is inert.
    setPhotos([]);
    setReceiptUploadId(null);
    setScannedKey(null);
    setScan({ kind: 'idle' });
  };

  /**
   * Job ↔ General. The photo was filed against the OTHER entity and the scan
   * finds it by that entity, so it is discarded rather than carried into a
   * lookup that cannot find it; the job, ticket and customer go with it.
   */
  const selectKind = (next: ExpenseKind) => {
    if (next === kind) return;
    setKind(next);
    selectJob('');
    setCustomerId('');
    setMeetingId('');
    setMeetingCustomerId('');
    retake();
  };

  const retryLink = () => {
    if (!photo || photoPending) return;
    linkAndScan(photo.key);
  };

  const createMutation = useMutation({
    mutationFn: (body: ExpenseCreateBody) => api.expenses.create(body as unknown as Record<string, unknown>),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      toast.success('Receipt filed — pending office approval');
      onFiled();
    },
    onError: (err) => setSubmitError(getApiErrorMessage(err, 'Could not file this receipt. Try again.')),
  });

  const amountNum = parseMoney(amount);
  const gstNum = gst.trim() === '' ? null : parseMoney(gst);
  const scanBusy = scan.kind === 'linking' || scan.kind === 'running';
  const missing: string[] = [];
  if (!photo) missing.push('a receipt photo');
  else if (photoPending) missing.push('the photo to finish uploading');
  else if (!receiptUploadId) missing.push('the photo to be linked');
  // A general receipt has no job by definition (0501); asking for one would
  // make it unfileable.
  if (!isGeneral && !jobId) missing.push('a job');
  if (!vendor.trim()) missing.push('the vendor');
  if (!date) missing.push('the date');
  // ── THE AMOUNT RULE DEPENDS ON WHO PAID (API 0389) ────────────────────────
  // $0 is legal on a company-paid receipt and illegal on a personal one, so
  // `amount` alone cannot answer this — the pair does. Mirrors amountPairError
  // in the API's expenses.routes.ts; the 400 is the backstop, this is the thing
  // that stops a hand submitting into one.
  //
  // ABSENT AND REFUSED ARE RENDERED SEPARATELY, on purpose. `missing` prints as
  // "Still needs a, b, c" — a list of things not yet supplied. A $0 personal
  // receipt is not missing anything; it is a value the rule refuses, and
  // dropping it into that list would tell a hand to go and enter the amount he
  // has already entered. It gets its own line, naming the rule and the remedy.
  if (amountNum === null) missing.push('an amount');
  const amountRuleError =
    amountNum !== null && amountNum < 0
      ? 'The amount cannot be negative.'
      : amountNum === 0 && paidBy !== 'company'
        ? `A $0 receipt has to be company-paid. If this is a fuel-card fill that printed litres ` +
          `and no price, set “Who paid?” to “${PAID_BY_LABELS.company}” and put the litres in Notes. ` +
          `A $0 personal receipt would be a reimbursement of nothing.`
        : null;
  if (gst.trim() !== '' && gstNum === null) missing.push('a valid GST amount');
  if (!category) missing.push('a category');
  const canSubmit = isOnline && missing.length === 0 && !amountRuleError && !scanBusy && !createMutation.isPending;

  const handleSubmit = () => {
    // `amountNum === null`, never `!amountNum`: 0 is a legal amount now and
    // falsy. canSubmit already covers the rule; this is the type narrowing.
    if (!canSubmit || !receiptUploadId || amountNum === null || !category) return;
    setSubmitError(null);
    const body: ExpenseCreateBody = {
      paid_by: paidBy,
      vendor: vendor.trim(),
      expense_date: date,
      amount: amountNum,
      category,
      receipt_upload_id: receiptUploadId,
    };
    // A general receipt carries NO job_id key at all — not '' and not null —
    // and states its kind; a job receipt keeps the exact body it always sent.
    if (isGeneral) {
      body.expense_kind = 'general';
      if (customerId) body.customer_id = customerId;
      if (meetingId) body.sales_activity_id = meetingId;
    } else {
      body.job_id = jobId;
    }
    if (gstNum !== null) body.gst_amount = gstNum;
    if (description.trim()) body.description = description.trim();
    // Omitted, not null, when nothing is chosen: the API distinguishes an
    // absent key from an explicit null and only one of them is "no ticket
    // was picked".
    if (ticketId && !isGeneral) body.ticket_id = ticketId;
    createMutation.mutate(body);
  };

  return (
    <div className={className ?? 'max-w-[1920px] mx-auto px-4 py-4 space-y-4'}>
        {!isOnline && (
          <div className="bg-orange-500/15 text-orange-300 rounded-xl px-4 py-3 text-sm flex items-start gap-2">
            <CloudOff size={16} className="mt-0.5 shrink-0" />
            <span>
              You're offline. A photo you take now is queued, but a receipt can only be scanned and filed
              with a connection.
            </span>
          </div>
        )}

        {/*
          * 1 — who paid. Asked before the camera: a hand knows this instantly,
          * it decides whether the receipt ever reaches payroll, and asking it
          * after the details would make it read like a footnote to a decision
          * already made.
          */}
        <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border space-y-3">
          <h3 className="font-semibold text-white">Who paid?</h3>
          <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="Who paid">
            {PAID_BY_VALUES.map((v) => (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={paidBy === v}
                onClick={() => setPaidBy(v)}
                className={`rounded-lg px-3 py-3 text-sm font-semibold border transition-colors ${
                  paidBy === v
                    ? 'bg-fieldiq-accent text-fieldiq-dark border-fieldiq-accent'
                    : 'bg-fieldiq-dark text-white border-fieldiq-border'
                }`}
              >
                {PAID_BY_LABELS[v]}
              </button>
            ))}
          </div>
          {/* The consequence, not the label again — the difference is money. */}
          <p className="text-xs text-fieldiq-muted">{PAID_BY_CONSEQUENCE[paidBy]}</p>
        </div>

        {/*
          * 1b — what was this for? OFFICE ONLY (D2). A field receipt is always
          * on a job, so the field tree never renders this control.
          */}
        {isOffice && (
          <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border space-y-3">
            <h3 className="font-semibold text-white">What was this for?</h3>
            <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="What was this for">
              {(['job', 'general'] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={kind === k}
                  onClick={() => selectKind(k)}
                  className={`rounded-lg px-3 py-3 text-sm font-semibold border transition-colors ${
                    kind === k
                      ? 'bg-fieldiq-accent text-fieldiq-dark border-fieldiq-accent'
                      : 'bg-fieldiq-dark text-white border-fieldiq-border'
                  }`}
                >
                  {EXPENSE_KIND_LABELS[k]}
                </button>
              ))}
            </div>
            <p className="text-xs text-fieldiq-muted">
              {isGeneral
                ? 'Not on any job — a sales lunch, a client visit. Name the customer if there was one.'
                : 'Filed against a job, and counted in that job’s cost.'}
            </p>
          </div>
        )}

        {/* 2 — the photo, keyed to the job (or, for General, to you). No job yet → no uploader yet. */}
        {receiptEntityId ? (
          <PhotoUploader
            entityType="expense_receipt"
            entityId={receiptEntityId}
            value={photos}
            onChange={setPhotos}
            variant="grid"
            maxPhotos={1}
            label="Receipt photo"
          />
        ) : (
          <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border text-sm text-fieldiq-text">
            Choose the job below first — the receipt photo is filed against it.
          </div>
        )}

        {/* 3 — what the scan said */}
        {scanBusy && (
          <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border flex items-center gap-3 text-sm text-fieldiq-accent">
            <Loader2 size={18} className="animate-spin" />
            {scan.kind === 'linking' ? 'Saving the photo…' : 'Reading the receipt…'}
          </div>
        )}
        {scan.kind === 'unlinked' && (
          <div className="bg-red-900/20 rounded-xl p-4 border border-red-500/30 text-sm text-red-300 space-y-2">
            <p className="flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {scan.message}
            </p>
            <div className="flex gap-2">
              <button onClick={retryLink} className="text-xs font-semibold bg-fieldiq-dark px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-white">
                <RefreshCw size={12} /> Retry
              </button>
              <button onClick={retake} className="text-xs font-semibold bg-fieldiq-dark px-3 py-1.5 rounded-lg text-white">
                Retake photo
              </button>
            </div>
          </div>
        )}
        {scan.kind === 'unreadable' && (
          <div
            role="alert"
            className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/30 text-sm text-amber-300 space-y-2"
          >
            <p className="flex items-start gap-2">
              <ScanLine size={16} className="mt-0.5 shrink-0" />
              <span>{OCR_MESSAGES.unreadable}{scan.message ? ` (${scan.message})` : ''}</span>
            </p>
            <button
              onClick={retake}
              className="text-xs font-semibold bg-amber-500 text-fieldiq-dark px-3 py-1.5 rounded-lg flex items-center gap-1.5"
            >
              <RefreshCw size={12} /> Retake photo
            </button>
          </div>
        )}
        {scan.kind === 'outage' && (
          <div role="alert" className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border text-sm text-fieldiq-text flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-400" />
            <span>{OCR_MESSAGES.outage}</span>
          </div>
        )}
        {scan.kind === 'failed' && (
          <div role="alert" className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border text-sm text-fieldiq-text flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-400" />
            <span>{OCR_MESSAGES.failed}{scan.message ? ` (${scan.message})` : ''}</span>
          </div>
        )}
        {scan.kind === 'ok' && (
          <div role="status" className="bg-green-500/10 rounded-xl p-4 border border-green-500/30 text-sm text-green-300 flex items-start gap-2">
            <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
            <span>
              Receipt read. Check the details below before you file it.
              {scan.prefill.categorySuggestion && !scan.prefill.category
                ? ` Suggested category "${scan.prefill.categorySuggestion}" isn't one of ours — pick the closest.`
                : ''}
              {scan.prefill.warnings.length > 0 && (
                <ul className="mt-1 list-disc pl-4 text-amber-300">
                  {scan.prefill.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              )}
            </span>
          </div>
        )}

        {/* 4 — the details, always editable */}
        <div className="bg-fieldiq-card rounded-xl p-4 space-y-3 border border-fieldiq-border">
          <h3 className="font-semibold text-white">Receipt details</h3>

          {isGeneral ? (
            <div>
              <label className="text-xs text-fieldiq-text mb-1 block" htmlFor="expense-customer">Customer (optional)</label>
              {customersFailure ? (
                // A failed load is not "no customers" — say which, and keep the
                // receipt fileable: the customer is optional.
                <p role="alert" className="text-sm text-amber-300">
                  Couldn't load customers. You can still file this receipt without one.
                </p>
              ) : (
                <select
                  id="expense-customer"
                  value={customerId}
                  onChange={(e) => selectCustomer(e.target.value)}
                  disabled={customersQuery.isLoading}
                  className={inputClass}
                >
                  <option value="">{customersQuery.isLoading ? 'Loading customers…' : 'No customer'}</option>
                  {customers.map((cu: any) => (
                    <option key={cu.id} value={cu.id}>{cu.name ?? cu.id}</option>
                  ))}
                </select>
              )}
              {meetingPicker && (
                <div className="mt-3">
                  <label className="text-xs text-fieldiq-text mb-1 block" htmlFor="expense-meeting">Meeting (optional)</label>
                  {meetingsFailure ? (
                    <p role="alert" className="text-sm text-fieldiq-text">
                      Couldn't load meetings. You can still file this receipt without one.
                    </p>
                  ) : (
                    <select
                      id="expense-meeting"
                      value={meetingId}
                      onChange={(e) => selectMeeting(e.target.value)}
                      disabled={meetingsQuery.isLoading}
                      className={inputClass}
                    >
                      <option value="">{meetingsQuery.isLoading ? 'Loading meetings…' : 'No meeting'}</option>
                      {meetings.map((m) => (
                        <option key={m.id} value={m.id}>
                          {[formatInstantDay(m.occurredAt, timeZone), m.customerName, salesActivityKindLabel(m.kind)]
                            .filter(Boolean)
                            .join(' · ')}
                        </option>
                      ))}
                      {/* A meeting passed in the link but not in this list still
                          shows as chosen, rather than the select silently
                          displaying "No meeting" while the id is sent. */}
                      {meetingId && !meetings.some((m) => m.id === meetingId) && (
                        <option value={meetingId}>The meeting you came from</option>
                      )}
                    </select>
                  )}
                </div>
              )}
            </div>
          ) : (
          <div>
            <label className="text-xs text-fieldiq-text mb-1 block" htmlFor="expense-job">Job</label>
            {jobFromParam && selectedJob ? (
              <div className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border text-white">
                {jobLabelOr(selectedJob, jobFromParam)}
              </div>
            ) : (
              <select
                id="expense-job"
                value={jobId}
                onChange={(e) => selectJob(e.target.value)}
                className={inputClass}
              >
                <option value="">Select a job…</option>
                {jobs.map((j: any) => (
                  <option key={j.id} value={j.id}>{jobLabelOr(j, j.id)}</option>
                ))}
                {jobId && !selectedJob && <option value={jobId}>{jobId}</option>}
              </select>
            )}
          </div>
          )}

          {/*
            * Optional, and never a gate: a receipt is photographed before the
            * day's ticket exists, so "no tickets on this job yet" is the
            * ordinary case and does not appear in `missing`.
            */}
          {jobId && !isGeneral && (
            <div>
              <label className="text-xs text-fieldiq-text mb-1 block" htmlFor="expense-ticket">Ticket (optional)</label>
              <TicketSelect
                id="expense-ticket"
                jobId={jobId}
                value={ticketId}
                onChange={setTicketId}
                selectClassName={inputClass}
                noteClassName="text-sm text-fieldiq-muted"
              />
            </div>
          )}

          <div>
            <label className="text-xs text-fieldiq-text mb-1 block" htmlFor="expense-vendor">Vendor</label>
            <input
              id="expense-vendor"
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              className={inputClass}
              placeholder="Who you paid"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-fieldiq-text mb-1 block">Date</label>
              <DatePicker value={date} onChange={setDate} placeholder="Receipt date" />
            </div>
            <div>
              <label className="text-xs text-fieldiq-text mb-1 block" htmlFor="expense-category">Category</label>
              <select
                id="expense-category"
                value={category}
                onChange={(e) => setCategory(e.target.value as ExpenseCategory | '')}
                className={inputClass}
              >
                <option value="">Select…</option>
                {EXPENSE_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{EXPENSE_CATEGORY_LABELS[c]}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-fieldiq-text mb-1 block" htmlFor="expense-amount">Total ($)</label>
              <input
                id="expense-amount"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={inputClass}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="text-xs text-fieldiq-text mb-1 block" htmlFor="expense-gst">GST ($)</label>
              <input
                id="expense-gst"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={gst}
                onChange={(e) => setGst(e.target.value)}
                className={inputClass}
                placeholder="Optional"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-fieldiq-text mb-1 block" htmlFor="expense-notes">Notes (optional)</label>
            <textarea
              id="expense-notes"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className={`${inputClass} resize-none`}
              placeholder="What it was for"
            />
          </div>

          {submitError && <p className="text-red-400 text-xs">{submitError}</p>}
          {/* A refused VALUE, not an absent one — red, and it names the remedy.
              Separate from the muted "Still needs" list below for that reason. */}
          {amountRuleError && !createMutation.isPending && (
            <p className="text-red-400 text-xs">{amountRuleError}</p>
          )}
          {!canSubmit && !createMutation.isPending && missing.length > 0 && (
            <p className="text-xs text-fieldiq-muted">Still needs {missing.join(', ')}.</p>
          )}

          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="w-full bg-fieldiq-accent text-fieldiq-dark font-semibold rounded-lg py-2.5 text-sm disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {createMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            File Receipt
          </button>
        </div>
    </div>
  );
}
